import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { api } from "../../lib/api";
import { FileDown, Send, Eye, XCircle, Ticket } from "lucide-react";

function useDebounce(value, delay = 400) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

const DAY_OPTIONS = [15, 30, 60, 90, 180];

export default function BookingInvoiceList() {
  const [list, setList] = useState([]);
  const [meta, setMeta] = useState({ total: 0, page: 1, limit: 20, totalPages: 1 });
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [search, setSearch] = useState("");
  const [days, setDays] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [sendingId, setSendingId] = useState(null);

  const debouncedSearch = useDebounce(search, 400);

  const fetchInvoices = async (opts = {}) => {
    const nextPage = opts.page ?? page;
    const nextLimit = opts.limit ?? limit;
    setLoading(true);
    setErr("");
    try {
      const params = {
        page: nextPage,
        limit: nextLimit,
      };
      if (debouncedSearch) params.search = debouncedSearch;
      if (days && DAY_OPTIONS.includes(Number(days))) params.days = days;
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;

      const res = await api.get("/admin/booking-invoices", { params });
      setList(res.data?.data || []);
      setMeta(res.data?.meta || { total: 0, page: nextPage, limit: nextLimit, totalPages: 1 });
    } catch (e) {
      const msg = e?.response?.data?.message || e.message || "Failed to load booking invoices";
      setErr(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setPage(1);
    fetchInvoices({ page: 1 });
  }, [debouncedSearch, days, startDate, endDate]);

  const handlePage = (nextPage) => {
    if (nextPage < 1 || nextPage > meta.totalPages) return;
    setPage(nextPage);
    fetchInvoices({ page: nextPage });
  };

  const handleLimitChange = (e) => {
    const v = Number(e.target.value) || 20;
    setLimit(v);
    setPage(1);
    fetchInvoices({ page: 1, limit: v });
  };

  const handleDownload = async () => {
    try {
      const params = {};
      if (debouncedSearch) params.search = debouncedSearch;
      if (days && DAY_OPTIONS.includes(Number(days))) params.days = days;
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;

      const res = await api.get("/admin/booking-invoices/download", {
        params,
        responseType: "blob",
        // Bulk export over an admin-chosen date range with no row cap — can
        // exceed the shared instance's 25s default. 120s override, scoped to
        // this call only.
        timeout: 120000,
      });
      const blob = new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "booking-invoices.xlsx";
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Download started");
    } catch (e) {
      const msg = e?.response?.data?.message || e.message || "Download failed";
      toast.error(msg);
    }
  };

  const handleDownloadOne = async (traceId) => {
    if (!traceId) return;
    try {
      const res = await api.get("/admin/booking-invoices/download", {
        params: { trace_id: traceId },
        responseType: "blob",
      });
      const blob = new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `invoice-${traceId}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Download started");
    } catch (e) {
      const msg = e?.response?.data?.message || e.message || "Download failed";
      toast.error(msg);
    }
  };

  // Fetches a PDF for the row's trace_id and opens it in a new tab; a JSON
  // error body (blob) is decoded and shown as a toast.
  const openPdfPreview = async (path, row, loadingText, timeout) => {
    const traceId = row?.trace_id;
    if (!traceId) return;
    const toastId = toast.loading(loadingText);
    try {
      const res = await api.get(path, {
        params: { trace_id: traceId },
        responseType: "blob",
        ...(timeout ? { timeout } : {}),
      });

      // If server returned an error (content-type is JSON not PDF), parse and show it
      const contentType = res.headers?.["content-type"] || "";
      if (!contentType.includes("application/pdf")) {
        const text = await res.data.text();
        let msg = "Preview failed";
        try { msg = JSON.parse(text)?.message || msg; } catch {}
        toast.error(msg, { id: toastId });
        return;
      }

      const url = window.URL.createObjectURL(res.data);
      toast.dismiss(toastId);
      window.open(url, "_blank", "noopener,noreferrer");
      // Release the object URL after the tab opens
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (e) {
      // Decode blob error body if present
      let msg = e.message || "Preview failed";
      if (e?.response?.data instanceof Blob) {
        try {
          const text = await e.response.data.text();
          msg = JSON.parse(text)?.message || msg;
        } catch {}
      }
      toast.error(msg, { id: toastId });
    }
  };

  const handlePreview = (row) =>
    openPdfPreview("/admin/booking-invoices/preview", row, "Generating PDF preview…");

  // Ticket PDF is built live from the supplier (same as the customer's
  // download), so it can exceed the shared 25s default; 60s for this call only.
  const handleTicketPreview = (row) =>
    openPdfPreview("/admin/booking-invoices/ticket/preview", row, "Generating ticket preview…", 60000);

  const handleCancellationPreview = (row) =>
    openPdfPreview("/admin/booking-invoices/cancellation/preview", row, "Generating cancellation invoice…");

  const handleCancellationSend = async (row) => {
    const traceId = row?.trace_id;
    if (!traceId) return;
    setSendingId(`cancel-${traceId}`);
    try {
      await api.post("/admin/booking-invoices/cancellation/send", { trace_id: traceId });
      toast.success("Cancellation invoice sent to " + (row.customer_name || "customer"));
    } catch (e) {
      const msg = e?.response?.data?.message || e.message || "Send failed";
      toast.error(msg);
    } finally {
      setSendingId(null);
    }
  };

  const handleSendOne = async (row) => {
    const traceId = row?.trace_id;
    if (!traceId) return;
    setSendingId(traceId);
    try {
      await api.post("/admin/booking-invoices/send", { trace_id: traceId });
      toast.success("Invoice sent to " + (row.customer_name || "customer"));
      fetchInvoices({ page });
    } catch (e) {
      const msg = e?.response?.data?.message || e.message || "Send failed";
      toast.error(msg);
    } finally {
      setSendingId(null);
    }
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <h1 className="text-2xl font-bold">Booking Invoice</h1>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => toast("Use the Send icon on each row to send that invoice")}
            className="rounded-xl border p-2.5 hover:bg-gray-50"
            title="Send invoice"
          >
            <Send className="w-5 h-5 text-gray-700" />
          </button>
          <button
            type="button"
            onClick={handleDownload}
            className="rounded-xl border p-2.5 hover:bg-gray-50"
            title="Download invoice"
          >
            <FileDown className="w-5 h-5 text-gray-700" />
          </button>
        </div>
      </div>

      <div className="mt-6 flex flex-wrap items-end gap-3 border rounded-2xl p-4">
        <div className="w-full sm:w-auto sm:min-w-[200px]">
          <label className="text-sm font-semibold">Search</label>
          <input
            className="mt-2 w-full border rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-black"
            placeholder="Ticket # / Trace / PNR / Payment ID..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="w-full sm:w-auto sm:min-w-[140px]">
          <label className="text-sm font-semibold">Days</label>
          <select
            className="mt-2 w-full border rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-black"
            value={days}
            onChange={(e) => setDays(e.target.value)}
          >
            <option value="">All</option>
            {DAY_OPTIONS.map((d) => (
              <option key={d} value={d}>
                Last {d} days
              </option>
            ))}
          </select>
        </div>
        <div className="w-full sm:w-auto sm:min-w-[160px]">
          <label className="text-sm font-semibold">Start date</label>
          <input
            type="date"
            className="mt-2 w-full border rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-black"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
        </div>
        <div className="w-full sm:w-auto sm:min-w-[160px]">
          <label className="text-sm font-semibold">End date</label>
          <input
            type="date"
            className="mt-2 w-full border rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-black"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </div>
        <div className="w-full sm:w-auto sm:min-w-[120px]">
          <label className="text-sm font-semibold">Per page</label>
          <select
            className="mt-2 w-full border rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-black"
            value={limit}
            onChange={handleLimitChange}
          >
            <option value="10">10</option>
            <option value="20">20</option>
            <option value="50">50</option>
            <option value="100">100</option>
          </select>
        </div>
      </div>

      {err ? <div className="mt-4 text-sm text-red-600">{err}</div> : null}

      <div className="mt-6 border rounded-2xl overflow-hidden">
        <div className="bg-gray-50 px-4 py-3 text-sm font-semibold flex flex-col sm:flex-row sm:items-center justify-between gap-2">
          <span>Invoices</span>
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <button
              type="button"
              onClick={() => handlePage(meta.page - 1)}
              disabled={meta.page <= 1 || loading}
              className="rounded-lg border px-3 py-1 font-semibold disabled:opacity-50"
            >
              Prev
            </button>
            <span className="text-gray-700">
              Page <b>{meta.page}</b> / <b>{meta.totalPages}</b> (Total: <b>{meta.total}</b>)
            </span>
            <button
              type="button"
              onClick={() => handlePage(meta.page + 1)}
              disabled={meta.page >= meta.totalPages || loading}
              className="rounded-lg border px-3 py-1 font-semibold disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>

        {loading ? (
          <div className="p-4 text-sm text-gray-600">Loading...</div>
        ) : list.length === 0 ? (
          <div className="p-4 text-sm text-gray-600">No invoices found.</div>
        ) : (
          <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-t bg-white">
              <tr className="text-left">
                <th className="px-4 py-3">Ticket #</th>
                <th className="px-4 py-3">Customer Name</th>
                {/* <th className="px-4 py-3">GST Number</th> */}
                {/* <th className="px-4 py-3">Place of Supply</th> */}
                <th className="px-4 py-3">Razorpay Payment ID</th>
                <th className="px-4 py-3 w-36">Actions</th>
              </tr>
            </thead>
            <tbody className="border-t">
              {list.map((row) => (
                <tr key={row.trace_id || row.booking_id} className="border-t hover:bg-gray-50">
                  <td className="px-4 py-3">{row.booking_ticket_number || "-"}</td>
                  <td className="px-4 py-3">{row.customer_name || "-"}</td>
                  {/* <td className="px-4 py-3">{row.gst_number || "-"}</td> */}
                  {/* <td className="px-4 py-3">{row.place_of_supply || "-"}</td> */}
                  <td className="px-4 py-3">{row.razorpay_payment_id || "-"}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => handlePreview(row)}
                        className="rounded-lg border p-2 hover:bg-blue-50"
                        title="Preview PDF"
                      >
                        <Eye className="w-4 h-4 text-blue-600" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleTicketPreview(row)}
                        className="rounded-lg border p-2 hover:bg-blue-50"
                        title="Preview ticket"
                      >
                        <Ticket className="w-4 h-4 text-blue-600" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDownloadOne(row.trace_id)}
                        className="rounded-lg border p-2 hover:bg-gray-100"
                        title="Download invoice"
                      >
                        <FileDown className="w-4 h-4 text-gray-700" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleSendOne(row)}
                        disabled={sendingId === row.trace_id}
                        className="rounded-lg border p-2 hover:bg-gray-100 disabled:opacity-50"
                        title="Send invoice"
                      >
                        <Send className="w-4 h-4 text-gray-700" />
                      </button>
                      {row.cancellation_type != null && row.cancellation_type !== 0 && (
                        <button
                          type="button"
                          onClick={() => handleCancellationPreview(row)}
                          disabled={sendingId === `cancel-${row.trace_id}`}
                          className="rounded-lg border p-2 hover:bg-red-50 disabled:opacity-50"
                          title="Generate cancellation invoice"
                        >
                          <XCircle className="w-4 h-4 text-red-600" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>
    </div>
  );
}
